import {
  Archive,
  Building2,
  Files,
  Image,
  Landmark,
  LayoutDashboard,
  Library,
  LogOut,
  Menu,
  MessageSquare,
  ScanQrCode,
  Settings2,
  Sparkles,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { cn } from '../lib/cn';

const mainLinks = [
  { to: '/app', end: true, label: 'المساعد العام', icon: MessageSquare },
  { to: '/app/jobs', label: 'المهام والمعالجة', icon: LayoutDashboard },
  { to: '/app/studio', label: 'الاستوديو العام', icon: Image },
  { to: '/app/architecture', label: 'العمارة المحلية', icon: Landmark },
  { to: '/app/knowledge', label: 'المعرفة والتوجيهات', icon: Library },
  { to: '/app/plans', label: 'تجهيز المخططات', icon: Files },
  { to: '/app/results', label: 'النتائج والأرشيف', icon: Archive },
  { to: '/app/verification', label: 'التحقق والأرشيف', icon: ScanQrCode },
  { to: '/app/admin', label: 'الإدارة والمزودون', icon: Settings2 },
];

const futureLinks = [
  { label: 'إخراج المخططات', icon: LayoutPanelIcon },
  { label: 'المخططات التخصصية', icon: Sparkles },
];

function LayoutPanelIcon(props: React.ComponentProps<'svg'>) {
  return <Building2 {...props} />;
}

export function PlatformLayout() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-[#f5f2ec] text-ink">
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-ink/10 bg-white/90 px-4 backdrop-blur-xl lg:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="grid h-10 w-10 place-items-center rounded-xl border border-ink/10"
          aria-label="فتح القائمة"
        >
          <Menu className="h-5 w-5" />
        </button>
        <span className="text-sm font-black">Details AI</span>
        <div className="h-9 w-9 rounded-full bg-teal text-center text-sm font-black leading-9 text-white">
          {user?.displayName?.charAt(0) || 'م'}
        </div>
      </header>

      <aside
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex w-72 transform flex-col border-l border-white/10 bg-ink text-white transition-transform lg:translate-x-0',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className="flex h-20 items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-teal text-lg font-black">د</div>
            <div>
              <p className="text-sm font-black">DETAILS AI</p>
              <p className="mt-0.5 text-[10px] font-bold text-white/45">مساحة العمل الهندسية</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="grid h-9 w-9 place-items-center rounded-lg bg-white/5 lg:hidden"
            aria-label="إغلاق القائمة"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-6">
          <p className="mb-2 px-3 text-[9px] font-black tracking-widest text-white/35">مساحة العمل</p>
          {mainLinks.map(({ to, end, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              {...(end !== undefined ? { end } : {})}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  'mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-xs font-bold text-white/65 transition hover:bg-white/[0.07] hover:text-white',
                  isActive && 'bg-teal text-white shadow-lg shadow-black/10',
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}

          <p className="mb-2 mt-6 px-3 text-[9px] font-black tracking-widest text-white/35">الأوضاع</p>
          {futureLinks.map(({ label, icon: Icon }) => (
            <button
              type="button"
              key={label}
              title="سيُفعّل في مرحلة لاحقة"
              className="mb-1 flex w-full cursor-default items-center gap-3 rounded-xl px-3 py-2.5 text-start text-xs font-bold text-white/30"
            >
              <Icon className="h-4 w-4" />
              {label}
              <span className="ms-auto rounded-full border border-white/10 px-1.5 py-0.5 text-[8px]">قريبًا</span>
            </button>
          ))}
        </nav>

        <div className="border-t border-white/10 p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-gold/20 text-sm font-black text-gold">
              {user?.displayName?.charAt(0) || 'م'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-black">{user?.displayName}</p>
              <p className="mt-0.5 text-[10px] text-white/40">مسؤول المنصة</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 py-2 text-[11px] font-bold text-white/60 transition hover:bg-white/5 hover:text-white"
          >
            <LogOut className="h-3.5 w-3.5" />
            تسجيل الخروج
          </button>
        </div>
      </aside>

      {open && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-sm lg:hidden"
          onClick={() => setOpen(false)}
          aria-label="إغلاق القائمة"
        />
      )}

      <main className="lg:mr-72">
        <Outlet />
      </main>
    </div>
  );
}
